import { AuthPageBody } from '../../components/AuthPageBody/AuthPageBody';
import {
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  FormLabel,
  Button,
  useToast,
  Icon,
  Text,
  Link as ChakraLink,
} from '@chakra-ui/react';
import { WithFormControl } from 'src/components/WithFormControl';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { forgotPasswordFormSchema } from './schemas/forgotPasswordFormSchema';
import { ForgotPasswordFormSchemaData } from './schemas/types';
import { AuthFormBaseContainer } from '@/components/AuthFormBaseContainer/AuthFormBaseContainer';
import { useNavigate } from 'react-router';
import { useForgotPasswordMutation } from '@/services/queries/auth';
import { PiUser } from 'react-icons/pi';
import { Link } from 'react-router-dom';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { useTranslation } from 'react-i18next';

export const ForgotPassword = (): JSX.Element => {
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<ForgotPasswordFormSchemaData>({
    resolver: zodResolver(forgotPasswordFormSchema),
  });
  const toast = useToast();
  const { t } = useTranslation();

  const navigate = useNavigate();
  const forgotPassswordEmailStepMutation = useForgotPasswordMutation();

  const onSubmit: SubmitHandler<ForgotPasswordFormSchemaData> = async data => {
    // Firebase envia o email de redefinição. Por segurança (anti-enumeração),
    // mostramos a mesma mensagem de sucesso mesmo se o email não existir.
    forgotPassswordEmailStepMutation.mutate(data.email, {
      onSuccess: () => {
        toast({
          status: 'success',
          title: t('pages.login.forgot_password.forgot_password_title'),
          description: t('pages.login.forgot_password.forgot_password_description'),
        });
        navigate('/login');
      },
      onError: (resp: any) => {
        toast({
          status: 'error',
          title: resp?.message ?? t('pages.login.forgot_password.forgot_password_error_title'),
          description: t('pages.login.forgot_password.forgot_password_error_description'),
        });
      },
    });
  };

  return (
    <AuthPageBody>
      <AuthFormBaseContainer
        title={t('pages.login.forgot_password.forgot_password_title')}
        description={t('pages.login.forgot_password.forgot_password_description')}
      >
        <Flex
          onSubmit={handleSubmit(onSubmit)}
          as='form'
          direction='column'
          align='stretch'
          gap='small'
          px={5}
          w='full'
          maxW='400px'
          alignSelf='center'
        >
          <Flex direction='column'>
            <WithFormControl variant='floating' errorMessage={errors.email?.message} isInvalid={!!errors.email}>
              <InputGroup>
                <Input variant='flushed' {...register('email')} placeholder=' ' />
                <FormLabel> {t('pages.login.forgot_password.forgot_password_email_label')}</FormLabel>

                <InputRightElement>
                  <Icon as={PiUser} width={4} height={4} />
                </InputRightElement>
              </InputGroup>
            </WithFormControl>

            <Text fontSize='16px' mt={4}>
              {t('pages.login.forgot_password.forgot_password_required_fields')}
            </Text>
          </Flex>

          <Flex alignItems='center' justifyContent='space-between' gap='8px'>
            <ChakraLink as={Link} to='/login' color='brand.500' fontSize='16px' textDecoration='underline'>
              {t('pages.login.forgot_password.forgot_password_back_link')}
            </ChakraLink>

            <Button type='submit' w='208px' alignSelf='end' variant='gradient' size='md'>
              {t('pages.login.forgot_password.forgot_password_submit_button')}
            </Button>
          </Flex>
        </Flex>
      </AuthFormBaseContainer>

      <CenteredSpinner isLoading={forgotPassswordEmailStepMutation?.isLoading} />
    </AuthPageBody>
  );
};
